import React from 'react';
import styled from 'styled-components';
import { PageCard } from 'src/common/PageCard';
import { Poppins } from 'src/common/Poppins';
import { SparkleSvg, RefreshSvg } from 'src/assets/dynamicSvg';
import { useRiskIntel } from '../useRiskIntel';
import { useLoadingAction } from '../useLoadingAction';
import { useVariablesPopover } from '../useVariablesPopover';
import { ImpactFormulaCard } from '../comps/ImpactFormulaCard';
import { PrimaryButton, SecondaryButton } from '../comps/SuggestionButtons';
import { StatusPill } from '../comps/StatusPill';

const Card = styled(PageCard)`
  background: ${({ theme }) => `${theme.colors.aliceBlue}4d`};
  border-radius: 8px;
  padding: 0;
  overflow: visible;
`;

const Eyebrow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 24px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.stroke};
`;

const EyebrowLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Footer = styled.div`
  display: flex;
  gap: 32px;
  padding: 24px 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.stroke};

  & > * {
    flex: 1 1 0;
  }
`;

export const ImpactFormulaSection: React.FC = () => {
  const { session, busy, acceptImpacts, changeImpacts } = useRiskIntel();
  const { loadingKey, run } = useLoadingAction();
  const { openVariables, popoverNode } = useVariablesPopover();
  const variables = session?.variables ?? [];
  const impacts = (session?.impacts ?? []).filter((impact) => impact.selected && impact.formula);
  const accepted = !!session?.impactsAccepted;

  if (impacts.length === 0) return null;

  return (
    <>
      <Card>
        <Eyebrow>
          <EyebrowLabel>
            <SparkleSvg size={16} />
            <Poppins px={16} weight={600} color="prussianBlue" css="text-transform: uppercase;">
              Suggested Impact Formula
            </Poppins>
          </EyebrowLabel>
        </Eyebrow>

        {impacts.map((impact, idx) => (
          <ImpactFormulaCard
            key={impact.id}
            impact={impact}
            variables={variables}
            first={idx === 0}
            onVariableClick={(id, el) => openVariables(el, id)}
          />
        ))}

        <Footer>
          {accepted ? (
            <StatusPill label="Accepted" />
          ) : (
            <PrimaryButton
              label="Accept suggestion"
              fullWidth
              loading={loadingKey === 'accept'}
              disabled={busy}
              onClick={() => run('accept', () => acceptImpacts())}
            />
          )}
          <SecondaryButton
            label="change impact selection"
            icon={<RefreshSvg size={16} />}
            fullWidth
            whiteBg
            loading={loadingKey === 'change'}
            disabled={busy}
            onClick={() => run('change', () => changeImpacts())}
          />
        </Footer>
      </Card>
      {popoverNode}
    </>
  );
};
